<template>
    <div
        v-if="
            visibleBestCards &&
            visibleBestCards.length > 0 &&
            visibleBestCards.some(
                (group) => group.cards && group.cards.length > 0
            )
        "
        class="w-full pt-24"
    >
        <h3 class="text-slate-800 dark:text-slate-100 text-3xl font-medium leading-9 mb-6">
            Best cards to earn {{ programName }}
        </h3>

        <div class="flex flex-col gap-8">
            <div
                v-for="userGroup in visibleBestCards"
                :key="userGroup.user_id"
                class="flex flex-col gap-6"
            >
                <div
                    v-if="visibleBestCards.length > 1"
                    class="flex items-center gap-2"
                >
                    <Icon icon="user" class="w-6 h-6 text-slate-800 dark:text-slate-100" />
                    <span
                        class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
                    >
                        {{ userGroup.user_name }}
                    </span>
                </div>

                <div
                    v-if="userGroup.cards.length === 0"
                    class="flex md:hidden items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                >
                    <Icon icon="circle-check" class="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span class="text-slate-600 dark:text-slate-300 text-sm">
                        You already have all recommended cards for this program in your wallet.
                    </span>
                </div>

                <!-- Mobile cards -->
                <div
                    class="flex flex-col items-start gap-4 w-full relative md:hidden"
                >
                    <div
                        v-for="(card, cardIndex) in userGroup.cards"
                        :key="card.id"
                        :class="[
                            'flex flex-col p-4 items-start w-full rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 relative overflow-hidden h-auto',
                            {
                                'shadow-none border-none':
                                    !userGroup.showingAll &&
                                    cardIndex === 1 &&
                                    userGroup.cards.length > 1,
                            },
                            {
                                'shadow-sm':
                                    userGroup.showingAll ||
                                    cardIndex === 0,
                            },
                        ]"
                    >
                        <div
                            v-if="
                                !userGroup.showingAll &&
                                cardIndex === 1 &&
                                userGroup.cards.length > 1
                            "
                            class="absolute inset-0 bg-gradient-to-b from-transparent from-0% via-slate-100/95 via-20% to-slate-100 to-35% dark:via-slate-800/95 dark:to-slate-800 pointer-events-none z-10"
                        ></div>

                        <div
                            v-if="
                                !userGroup.showingAll &&
                                cardIndex === 1 &&
                                userGroup.cards.length > 1
                            "
                            class="absolute inset-x-0 bottom-52 z-20 flex justify-center"
                        >
                            <button
                                @click="$emit('toggle-card-display', userGroup.user_id)"
                                class="flex py-2 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200"
                            >
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Show more
                                </span>
                            </button>
                        </div>

                        <div
                            class="w-full aspect-[79/50] rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-700 flex items-center justify-center mb-4"
                        >
                            <img
                                v-if="card.card_image"
                                :src="card.card_image"
                                :alt="card.title"
                                class="w-full h-full object-cover"
                            />
                            <div
                                v-else
                                class="w-full h-full bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex flex-col justify-between p-4"
                            >
                                <div
                                    class="flex justify-between items-start"
                                >
                                    <div
                                        class="text-white font-bold text-lg"
                                    >
                                        {{
                                            card.issuing_bank
                                                ? card.issuing_bank.toUpperCase()
                                                : 'CHASE'
                                        }}
                                    </div>
                                    <div
                                        class="text-white font-bold text-sm opacity-75"
                                    >
                                        {{
                                            card.card_type
                                                ? card.card_type.toUpperCase()
                                                : 'BUSINESS'
                                        }}
                                    </div>
                                </div>
                                <div
                                    class="flex justify-between items-end"
                                >
                                    <div
                                        class="text-white font-mono text-sm"
                                    >
                                        &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull;
                                    </div>
                                    <div
                                        class="text-white text-xs opacity-75"
                                    >
                                        VISA
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex flex-col items-start gap-2 w-full mb-4"
                        >
                            <a
                                :href="card.application_link"
                                target="_blank"
                                class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                            >
                                <span
                                    class="text-white text-sm font-medium leading-5"
                                >
                                    Learn how to apply
                                </span>
                                <Icon
                                    icon="arrow-right-alt"
                                    class="w-4 h-4 text-white"
                                />
                            </a>

                            <button
                                class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"
                            >
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Read our take
                                </span>
                            </button>
                        </div>

                        <div
                            class="flex w-full pb-4 flex-col items-start"
                        >
                            <h4
                                class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                            >
                                {{ card.title }}
                            </h4>
                        </div>

                        <div
                            class="flex pb-4 items-start gap-2 flex-wrap"
                        >
                            <div class="hidden">
                                <div
                                    v-if="card.card_type === 'personal'"
                                    class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                >
                                    <Icon
                                        icon="suitcase"
                                        class="w-4 h-4 text-slate-800 dark:text-slate-100"
                                    />
                                    <span
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                    >
                                        Travel
                                    </span>
                                </div>
                            </div>

                            <div
                                v-if="card.card_type === 'business'"
                                class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                            >
                                <Icon
                                    icon="business"
                                    class="w-4 h-4 text-slate-800 dark:text-slate-100"
                                />
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Business
                                </span>
                            </div>

                            <div
                                v-if="!!card.allow_five_twenty_four_rule"
                                class="flex py-1 px-3 items-center gap-2 rounded-full border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/20"
                            >
                                <Icon
                                    icon="credit-cards"
                                    class="w-4 h-4 text-orange"
                                />
                                <span
                                    class="text-orange text-sm font-medium leading-5"
                                >
                                    Card adds to your 5/24
                                </span>
                            </div>
                        </div>

                        <div
                            v-show="
                                userGroup.showingAll || cardIndex !== 1
                            "
                            class="flex flex-col items-start w-full divide-y divide-slate-200 dark:divide-slate-700"
                        >
                            <div
                                class="flex flex-col items-start gap-1 w-full py-4"
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                                >
                                    Welcome offer
                                </span>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                                    v-safe-html="highlightOffer(card.welcomeOffer)"
                                ></span>
                                <span
                                    class="text-slate-600 dark:text-slate-300 text-sm leading-5"
                                >
                                    {{ card.welcomeOfferDetails }}
                                </span>
                            </div>

                            <div
                                class="flex flex-col items-start gap-1 w-full py-4"
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                                >
                                    Annual fee
                                </span>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    {{ card.annualFee }}
                                    <template
                                        v-if="
                                            (card.issuing_bank ===
                                                'American Express' ||
                                                card.issuing_bank ===
                                                    'Citi') &&
                                            card.rates_and_fees_link
                                        "
                                    >
                                        <a
                                            :href="card.rates_and_fees_link"
                                            target="_blank"
                                            class="ml-1 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                        >
                                            (Rates and fees)
                                        </a>
                                    </template>
                                </span>
                            </div>

                            <div
                                class="flex flex-col items-start gap-2 w-full py-4"
                            >
                                <div class="flex items-center w-full">
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                                    >
                                        How to earn points
                                    </span>
                                    <button
                                        v-if="hasMoreEarnRates(card)"
                                        @click="toggleEarnRates(card.id)"
                                        class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded ml-auto"
                                    >
                                        <Icon
                                            :icon="
                                                isEarnRatesExpanded(card.id)
                                                    ? 'chevron-up'
                                                    : 'chevron-down'
                                            "
                                            class="w-4 h-4 text-slate-600 dark:text-slate-300"
                                        />
                                    </button>
                                </div>
                                <template
                                    v-if="getEffectiveEarnRates(card).length > 0"
                                >
                                    <div
                                        class="flex items-center content-start gap-3 flex-wrap w-full"
                                    >
                                        <div
                                            v-for="rate in visibleEarnRates(card)"
                                            :key="rate.category + '-' + rate.multiplier"
                                            class="flex items-center gap-2"
                                        >
                                            <div
                                                class="flex px-2 py-0.5 flex-col justify-center items-center rounded bg-orange-50 dark:bg-orange-500/20"
                                            >
                                                <span
                                                    class="text-orange text-sm font-semibold leading-5"
                                                >
                                                    {{ rate.multiplier }}
                                                </span>
                                            </div>
                                            <span
                                                class="text-slate-800 dark:text-slate-100 text-sm leading-5"
                                            >
                                                {{ rate.category_label || rate.category }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm leading-5"
                                    >
                                        No category bonuses available
                                        for this card at the moment.
                                    </span>
                                </template>
                            </div>

                            <div
                                class="flex flex-col items-start gap-2 w-full py-4"
                            >
                                <span
                                    class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                                >
                                    Benefits
                                </span>
                                <template v-if="getBenefitIcons(card).length">
                                    <div
                                        class="flex items-center gap-2 flex-wrap"
                                    >
                                        <div
                                            v-for="(iconName, idx) in getBenefitIcons(card)"
                                            :key="iconName + '-' + idx"
                                            class="flex w-9 h-9 justify-center items-center rounded-full bg-orange-50 dark:bg-orange-500/20"
                                        >
                                            <BenefitIcon
                                                :type="iconName"
                                                class="w-4 h-4 text-orange"
                                            />
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm leading-5"
                                    >
                                        There are currently no benefits
                                        available for this card.
                                    </span>
                                </template>
                            </div>
                        </div>

                        <div
                            class="flex flex-col items-start gap-2 w-full pt-4 mt-1 border-t border-slate-200 dark:border-slate-700"
                        >
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                10xT Thoughts
                            </span>
                            <div
                                class="flex flex-col items-start gap-1 w-full relative"
                            >
                                <div
                                    :class="[
                                        'text-slate-800 dark:text-slate-100 text-sm leading-5 transition-all duration-300',
                                        !isThoughtsExpanded(card.id) &&
                                        isThoughtsLong(card.thoughts)
                                            ? 'line-clamp-2'
                                            : '',
                                    ]"
                                >
                                    {{ card.thoughts }}
                                </div>

                                <div
                                    v-if="
                                        isThoughtsLong(card.thoughts) &&
                                        !isThoughtsExpanded(card.id)
                                    "
                                    class="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white via-white to-transparent dark:from-slate-800 dark:via-slate-800 pointer-events-none"
                                ></div>

                                <button
                                    v-if="isThoughtsLong(card.thoughts)"
                                    @click="toggleThoughts(card.id)"
                                    class="flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors duration-200 mt-1"
                                >
                                    <Icon
                                        :icon="
                                            isThoughtsExpanded(card.id)
                                                ? 'chevron-up'
                                                : 'chevron-down'
                                        "
                                        class="w-4 h-4 mr-1"
                                    />
                                    <span class="text-sm">
                                        {{
                                            isThoughtsExpanded(card.id)
                                                ? 'Show less'
                                                : 'Show more'
                                        }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Desktop empty state -->
                <div
                    v-if="userGroup.cards.length === 0"
                    class="hidden md:flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                >
                    <Icon icon="circle-check" class="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span class="text-slate-600 dark:text-slate-300 text-sm">
                        You already have all recommended cards for this program in your wallet.
                    </span>
                </div>

                <!-- Desktop cards -->
                <div
                    class="hidden md:flex flex-col items-start gap-4 w-full relative"
                >
                    <div
                        v-for="(card, cardIndex) in userGroup.cards"
                        :key="card.id"
                        :class="[
                            'flex p-6 items-start gap-8 w-full rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 relative overflow-hidden',
                            {
                                'max-h-[300px] shadow-none border-none':
                                    !userGroup.showingAll &&
                                    cardIndex === 1 &&
                                    userGroup.cards.length > 1,
                            },
                            {
                                'shadow-sm':
                                    userGroup.showingAll ||
                                    cardIndex === 0,
                            },
                        ]"
                    >
                        <div
                            v-if="
                                !userGroup.showingAll &&
                                cardIndex === 1 &&
                                userGroup.cards.length > 1
                            "
                            class="absolute inset-0 bg-gradient-to-b from-transparent from-30% via-slate-100/95 via-50% to-slate-100 to-60% dark:via-slate-800/95 dark:to-slate-800 pointer-events-none z-10"
                        ></div>

                        <div class="flex flex-col items-center gap-4">
                            <div class="flex flex-col items-start">
                                <div
                                    class="w-80 h-[198px] rounded-xl overflow-hidden bg-gray-100 dark:bg-slate-700 flex items-center justify-center"
                                >
                                    <img
                                        v-if="card.card_image"
                                        :src="card.card_image"
                                        :alt="card.title"
                                        class="w-full h-full object-contain"
                                    />
                                    <div
                                        v-else
                                        class="w-full h-full bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex flex-col justify-between p-4"
                                    >
                                        <div
                                            class="flex justify-between items-start"
                                        >
                                            <div
                                                class="text-white font-bold text-lg"
                                            >
                                                {{
                                                    card.issuing_bank
                                                        ? card.issuing_bank.toUpperCase()
                                                        : 'CHASE'
                                                }}
                                            </div>
                                            <div
                                                class="text-white font-bold text-sm opacity-75"
                                            >
                                                {{
                                                    card.card_type
                                                        ? card.card_type.toUpperCase()
                                                        : 'BUSINESS'
                                                }}
                                            </div>
                                        </div>
                                        <div
                                            class="flex justify-between items-end"
                                        >
                                            <div
                                                class="text-white font-mono text-sm"
                                            >
                                                &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull;
                                            </div>
                                            <div
                                                class="text-white text-xs opacity-75"
                                            >
                                                VISA
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="flex flex-col items-start gap-2 w-full mt-4"
                                >
                                    <a
                                        :href="card.application_link"
                                        target="_blank"
                                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                                    >
                                        <span
                                            class="text-white text-sm font-medium leading-5"
                                        >
                                            Learn how to apply
                                        </span>
                                        <Icon
                                            icon="arrow-right-alt"
                                            class="w-4 h-4 text-white"
                                        />
                                    </a>

                                    <button
                                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                                    >
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                        >
                                            Read our take
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex flex-col justify-center items-start flex-1"
                        >
                            <div
                                class="flex w-full pb-4 flex-col items-start"
                            >
                                <h4
                                    class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                                >
                                    {{ card.title }}
                                </h4>
                            </div>
                            <div
                                class="flex pb-8 items-start gap-2 flex-wrap"
                            >
                                <div class="hidden">
                                    <div
                                        v-if="card.card_type === 'personal'"
                                        class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                    >
                                        <Icon
                                            icon="suitcase"
                                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                                        />
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                        >
                                            Travel
                                        </span>
                                    </div>
                                </div>

                                <div
                                    v-if="card.card_type === 'business'"
                                    class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                >
                                    <Icon
                                        icon="business"
                                        class="w-4 h-4 text-slate-800 dark:text-slate-100"
                                    />
                                    <span
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                    >
                                        Business
                                    </span>
                                </div>

                                <div
                                    v-if="!!card.allow_five_twenty_four_rule"
                                    class="flex py-1 px-3 items-center gap-2 rounded-full border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/20"
                                >
                                    <Icon
                                        icon="credit-cards"
                                        class="w-4 h-4 text-orange"
                                    />
                                    <span
                                        class="text-orange text-sm font-medium leading-5"
                                    >
                                        Card adds to your 5/24
                                    </span>
                                </div>
                            </div>

                            <div
                                class="flex flex-col items-start w-full"
                            >
                                <div
                                    class="flex pb-4 items-start gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                                >
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 min-w-[120px]"
                                    >
                                        Welcome offer
                                    </span>
                                    <div
                                        class="flex flex-col items-start gap-1 flex-1"
                                    >
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                                            v-safe-html="highlightOffer(card.welcomeOffer)"
                                        ></span>
                                        <span
                                            class="text-slate-600 dark:text-slate-300 text-sm leading-5"
                                        >
                                            {{ card.welcomeOfferDetails }}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    class="flex py-[18px] items-center gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                                >
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 min-w-[120px]"
                                    >
                                        Annual fee
                                    </span>
                                    <div
                                        class="flex items-end gap-1 flex-1"
                                    >
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                        >
                                            {{ card.annualFee }}
                                        </span>
                                        <template
                                            v-if="
                                                (card.issuing_bank ===
                                                    'American Express' ||
                                                    card.issuing_bank ===
                                                        'Citi') &&
                                                card.rates_and_fees_link
                                            "
                                        >
                                            <a
                                                :href="card.rates_and_fees_link"
                                                target="_blank"
                                                class="ml-1 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                            >
                                                (Rates and fees)
                                            </a>
                                        </template>
                                    </div>
                                </div>

                                <div
                                    class="flex py-4 items-center gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                                >
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 min-w-[120px]"
                                    >
                                        How to earn points
                                    </span>
                                    <div
                                        class="flex items-center content-start gap-4 flex-1 flex-wrap"
                                    >
                                        <template
                                            v-if="getEffectiveEarnRates(card).length > 0"
                                        >
                                            <div
                                                v-for="rate in visibleEarnRates(card)"
                                                :key="rate.category + '-' + rate.multiplier"
                                                class="flex items-center gap-2"
                                            >
                                                <div
                                                    class="flex px-2 py-0.5 flex-col justify-center items-center rounded bg-orange-50 dark:bg-orange-500/20"
                                                >
                                                    <span
                                                        class="text-orange text-sm font-semibold leading-5"
                                                    >
                                                        {{ rate.multiplier }}
                                                    </span>
                                                </div>
                                                <span
                                                    class="text-slate-800 dark:text-slate-100 text-sm leading-5"
                                                >
                                                    {{ rate.category_label || rate.category }}
                                                </span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <span
                                                class="text-slate-500 dark:text-slate-400 text-sm leading-5"
                                            >
                                                No category bonuses
                                                available for this card
                                                at the moment.
                                            </span>
                                        </template>
                                    </div>
                                    <button
                                        v-if="hasMoreEarnRates(card)"
                                        @click="toggleEarnRates(card.id)"
                                        class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                                    >
                                        <Icon
                                            :icon="
                                                isEarnRatesExpanded(card.id)
                                                    ? 'chevron-up'
                                                    : 'chevron-down'
                                            "
                                            class="w-4 h-4 text-slate-600 dark:text-slate-300"
                                        />
                                    </button>
                                </div>

                                <div
                                    class="flex py-4 items-center gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                                >
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 min-w-[120px]"
                                    >
                                        Benefits
                                    </span>
                                    <div
                                        class="flex items-start gap-4 flex-1"
                                    >
                                        <template v-if="getBenefitIcons(card).length">
                                            <div
                                                class="flex items-center gap-3 flex-wrap"
                                            >
                                                <div
                                                    v-for="(iconName, idx) in getBenefitIcons(card)"
                                                    :key="iconName + '-' + idx"
                                                    class="flex w-9 h-9 justify-center items-center rounded-full bg-orange-50 dark:bg-orange-500/20"
                                                >
                                                    <BenefitIcon
                                                        :type="iconName"
                                                        class="w-4 h-4 text-orange"
                                                    />
                                                </div>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <span
                                                class="text-slate-500 dark:text-slate-400 text-sm leading-5"
                                            >
                                                There are currently no
                                                benefits available for
                                                this card.
                                            </span>
                                        </template>
                                    </div>
                                </div>

                                <div
                                    class="flex py-4 items-start gap-6 w-full"
                                >
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 min-w-[120px]"
                                    >
                                        10xT Thoughts
                                    </span>
                                    <div
                                        class="flex flex-col items-start gap-1 flex-1 relative"
                                    >
                                        <div
                                            :class="[
                                                'text-slate-800 dark:text-slate-100 text-sm leading-5 transition-all duration-300',
                                                !isThoughtsExpanded(card.id) &&
                                                isThoughtsLong(card.thoughts)
                                                    ? 'line-clamp-2'
                                                    : '',
                                            ]"
                                        >
                                            {{ card.thoughts }}
                                        </div>

                                        <div
                                            v-if="
                                                isThoughtsLong(card.thoughts) &&
                                                !isThoughtsExpanded(card.id)
                                            "
                                            class="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white via-white to-transparent dark:from-slate-800 dark:via-slate-800 pointer-events-none"
                                        ></div>
                                    </div>

                                    <div
                                        v-if="isThoughtsLong(card.thoughts)"
                                        class="flex-shrink-0"
                                    >
                                        <button
                                            @click="toggleThoughts(card.id)"
                                            class="flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors duration-200"
                                        >
                                            <Icon
                                                :icon="
                                                    isThoughtsExpanded(card.id)
                                                        ? 'chevron-up'
                                                        : 'chevron-down'
                                                "
                                                class="w-4 h-4"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Desktop show more/less -->
                <div
                    class="hidden md:flex flex-col items-start gap-4 w-full relative"
                >
                    <div
                        v-if="
                            !userGroup.showingAll &&
                            userGroup.cards.length > 1
                        "
                        class="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
                    >
                        <button
                            @click="$emit('toggle-card-display', userGroup.user_id)"
                            class="flex py-2 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200"
                        >
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Show more
                            </span>
                        </button>
                    </div>

                    <div
                        v-if="
                            userGroup.showingAll &&
                            userGroup.cards.length > 1
                        "
                        class="flex w-full pt-6 flex-col items-center gap-2.5"
                    >
                        <button
                            @click="$emit('toggle-card-display', userGroup.user_id)"
                            class="flex py-2 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200"
                        >
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Show less
                            </span>
                        </button>

                        <button
                            @click="$emit('redirect-next-card')"
                            class="flex py-2 px-4 justify-center items-center gap-2 rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                        >
                            <span
                                class="text-white text-sm font-medium leading-5"
                            >
                                Find your next card
                            </span>
                            <Icon
                                icon="chevron-right-small"
                                class="w-4 h-4 text-white"
                            />
                        </button>
                    </div>
                </div>

                <!-- Mobile show more/less -->
                <div class="md:hidden">
                    <div
                        v-if="
                            userGroup.showingAll &&
                            userGroup.cards.length > 1
                        "
                        class="flex w-full pt-6 flex-col items-center gap-2.5"
                    >
                        <button
                            @click="$emit('toggle-card-display', userGroup.user_id)"
                            class="flex py-2 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-200"
                        >
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Show less
                            </span>
                        </button>

                        <button
                            @click="$emit('redirect-next-card')"
                            class="flex py-2 px-4 justify-center items-center gap-2 rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                        >
                            <span
                                class="text-white text-sm font-medium leading-5"
                            >
                                Find your next card
                            </span>
                            <Icon
                                icon="chevron-right-small"
                                class="w-4 h-4 text-white"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import BenefitIcon from '@/components/ui/icon/BenefitIcon.vue';

defineProps({
    visibleBestCards: Array,
    programName: String,
    highlightOffer: Function,
    getEffectiveEarnRates: Function,
    visibleEarnRates: Function,
    hasMoreEarnRates: Function,
    isEarnRatesExpanded: Function,
    toggleEarnRates: Function,
    getBenefitIcons: Function,
    isThoughtsLong: Function,
    isThoughtsExpanded: Function,
    toggleThoughts: Function,
});

defineEmits(['toggle-card-display', 'redirect-next-card']);
</script>
